import { forwardRef, type MouseEvent } from 'react';
import { cn } from '../../lib/utils';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { IconExternalLink } from '@tabler/icons-react';
import useI18n from '../../hooks/useI18n';
import type { LinkProps } from './types';

const sizes = {
  default: 'eye:body-l-highlight eye:underline',
  small: 'eye:body-m eye:underline',
  medium:
    'eye:body-m eye:pt-1 eye:no-underline eye:font-semibold eye:hover:underline',
};

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    href,
    children,
    size = 'default',
    className,
    isExternal = false,
    disabled = false,
    asChild = false,
    ...props
  }: LinkProps,
  ref,
) {
  const { t } = useI18n();

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    if (disabled) {
      e.preventDefault();
    }
  };

  const Component = asChild ? Slot : 'a';

  const externalIcon = isExternal && (
    <span className='eye:ml-5xs eye:inline-flex eye:items-center eye:align-middle'>
      <IconExternalLink
        role='img'
        aria-label={t('opensNewPage')}
        className={cn(
          size === 'small' || size === 'medium'
            ? 'eye:icon-small'
            : 'eye:icon-big',
          'eye:flex-none',
        )}
      />
    </span>
  );

  return (
    <Component
      ref={ref}
      href={disabled ? undefined : href}
      target={isExternal && !disabled ? '_blank' : undefined}
      rel={isExternal && !disabled ? 'noopener noreferrer' : undefined}
      className={cn(
        sizes[size],
        'eye:decoration-skip-ink eye:inline eye:underline-offset-2 eye:focus:outline-2 eye:focus:outline-offset-2 eye:focus:outline-current',
        !disabled && 'eye:cursor-pointer eye:hover:text-grey-120',
        disabled && 'eye:cursor-not-allowed eye:[&]:text-grey-disabled',
        className,
      )}
      onClick={handleClick}
      {...props}
    >
      {/* Slottable places the icon inside the child element, next to its own content. */}
      {asChild ? (
        <Slottable>{children}</Slottable>
      ) : (
        <span className='eye:inline eye:align-middle'>{children}</span>
      )}
      {externalIcon}
    </Component>
  );
});

export { Link };
